import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader/root';
import { compose } from 'redux';

import { TrackEventContext } from 'common/containers/EventContainer';
import LandingFeaturesHelmet from 'common/helmets/LandingFeaturesHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import Link from 'common/Link';
import withContexts from 'common/util/withContexts';
import CustomizeImage from 'img/landing/features-customize.webp';
import ETAImage from 'img/landing/features-eta.webp';
import InternalCommentsImage from 'img/landing/features-internal-comments.webp';
import LabelsImage from 'img/landing/features-labels.webp';
import MarkdownImage from 'img/landing/features-markdown.webp';
import MRRImage from 'img/landing/features-mrr.webp';
import PinImage from 'img/landing/features-pin.webp';
import PrioritizationImage from 'img/landing/features-prioritization.webp';
import PrivacyImage from 'img/landing/features-privacy.webp';
import ProfilesImage from 'img/landing/features-profiles.webp';
import RoadmappingImage from 'img/landing/features-roadmapping.webp';
import ScheduleImage from 'img/landing/features-schedule.webp';
import SegmentVotesImage from 'img/landing/features-segment-votes.webp';
import SegmentationImage from 'img/landing/features-segmentation.webp';
import Step1 from 'img/landing/features-step-1.webp';
import Step2 from 'img/landing/features-step-2.webp';
import Step3 from 'img/landing/features-step-3.webp';
import Step4 from 'img/landing/features-step-4.webp';
import TagsImage from 'img/landing/features-tags.webp';
import UpdatesImage from 'img/landing/features-updates.webp';
import VoteAsImage from 'img/landing/features-vote-as.webp';
import WidgetImage from 'img/landing/features-widget.webp';
import FeedbackLoop from 'img/landing/feedback-loop.webp';

import AnalyzeImage from 'img/features-page-analyze.jpg';
import ChangelogImage from 'img/features-page-changelog.jpg';
import FeedbackImage from 'img/features-page-feedback.jpg';
import RoadmapImage from 'img/features-page-roadmap.jpg';

import 'css/components/www/_LandingFeaturesPage.scss';

class LandingFeaturesPage extends Component {
  static propTypes = {
    trackEvent: PropTypes.func,
  };

  componentDidMount() {
    const { trackEvent } = this.props;
    trackEvent('Viewed Features Page');
  }

  render() {
    return (
      <div className="landingFeaturesPage">
        <LandingFeaturesHelmet />
        <div className="landingFeaturesPageContainer">
          <div className="header">
            <div className="text">
              <h1 className="title">Canny features</h1>
              <span className="subtitle">
                Everything you need to navigate the customer feedback&nbsp;loop
              </span>
            </div>
            <LazyLoadedImage
              loading="eager"
              src={FeedbackLoop}
              alt="Canny's features for collecting feedback and roadmapping create a complete feedback loop"
            />
          </div>
          <div className="featuresContainer">
            <div className="left">
              <LazyLoadedImage loading="eager" src={Step1} />
              <h2 className="sectionHeader">Feedback</h2>
              <div className="sectionDescription">
                Capture insights from your customers and teammates in one organized&nbsp;place.
              </div>
              <Link to="/features/feedback" className="LandingGlobal__cta">
                Read more
              </Link>
            </div>
            <div className="right">
              <LazyLoadedImage
                className="sectionImage"
                loading="eager"
                src={FeedbackImage}
                alt="Canny features that help collect feedback"
              />
              <div className="featuresGrid">
                <div className="feature">
                  <div className="imageContainer">
                    <LazyLoadedImage src={ProfilesImage} />
                  </div>
                  <div className="text">
                    <div className="title">Identify Users</div>
                    <div className="description">
                      Automatically tie feedback to your existing user&nbsp;accounts
                    </div>
                  </div>
                </div>
                <div className="feature">
                  <div className="imageContainer">
                    <LazyLoadedImage src={PrivacyImage} />
                  </div>
                  <div className="text">
                    <div className="title">Board Privacy</div>
                    <div className="description">
                      Choose different privacy options to limit access to your&nbsp;boards
                    </div>
                  </div>
                </div>
                <div className="feature">
                  <div className="imageContainer">
                    <LazyLoadedImage src={VoteAsImage} />
                  </div>
                  <div className="text">
                    <div className="title">Vote on Behalf</div>
                    <div className="description">
                      Track feedback manually by adding a customer to a&nbsp;post
                    </div>
                  </div>
                </div>
                <div className="feature">
                  <div className="imageContainer">
                    <LazyLoadedImage src={InternalCommentsImage} />
                  </div>
                  <div className="text">
                    <div className="title">Internal Comments</div>
                    <div className="description">
                      Discuss ideas privately with your team while keeping everything
                      in&nbsp;context
                    </div>
                  </div>
                </div>
                <div className="feature">
                  <div className="imageContainer">
                    <LazyLoadedImage src={PinImage} />
                  </div>
                  <div className="text">
                    <div className="title">Pinned Comments</div>
                    <div className="description">
                      Make sure important responses are seen at the&nbsp;top
                    </div>
                  </div>
                </div>
                <div className="feature">
                  <div className="imageContainer">
                    <LazyLoadedImage src={CustomizeImage} />
                  </div>
                  <div className="text">
                    <div className="title">Customizations</div>
                    <div className="description">
                      Feel at home with your logo, brand colors, and&nbsp;prompts
                    </div>
                  </div>
                </div>
                <div className="feature">
                  <div className="imageContainer">
                    <LazyLoadedImage src={MarkdownImage} />
                  </div>
                  <div className="text">
                    <div className="title">Markdown</div>
                    <div className="description">
                      Easily format posts and comments with rich text for&nbsp;readability
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="featuresContainer">
            <div className="left">
              <LazyLoadedImage src={Step2} />
              <h2 className="sectionHeader">Analyze</h2>
              <div className="sectionDescription">
                Sort and filter feedback data to inform your product&nbsp;decisions.
              </div>
              <Link to="/features/roadmap" className="LandingGlobal__cta">
                Read more
              </Link>
            </div>
            <div className="right">
              <LazyLoadedImage
                className="sectionImage"
                src={AnalyzeImage}
                alt="Canny features for showing off your product roadmap"
              />
              <div className="featuresGrid">
                <div className="feature">
                  <div className="imageContainer">
                    <LazyLoadedImage src={SegmentationImage} />
                  </div>
                  <div className="text">
                    <div className="title">User Segmentation</div>
                    <div className="description">
                      Filter feedback to user groups you care about e.g. paying&nbsp;customers
                    </div>
                  </div>
                </div>
                <div className="feature">
                  <div className="imageContainer">
                    <LazyLoadedImage src={TagsImage} />
                  </div>
                  <div className="text">
                    <div className="title">Tags & Categories</div>
                    <div className="description">
                      Add deeper levels of organization to your&nbsp;posts
                    </div>
                  </div>
                </div>
                <div className="feature">
                  <div className="imageContainer">
                    <LazyLoadedImage src={SegmentVotesImage} />
                  </div>
                  <div className="text">
                    <div className="title">Segment Voters</div>
                    <div className="description">
                      Understand which user segments are voting on&nbsp;posts
                    </div>
                  </div>
                </div>
                <div className="feature">
                  <div className="imageContainer">
                    <LazyLoadedImage src={MRRImage} />
                  </div>
                  <div className="text">
                    <div className="title">Sort by MRR Impact</div>
                    <div className="description">
                      Keep track of the amount of revenue a feature&nbsp;impacts
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="featuresContainer">
            <div className="left">
              <LazyLoadedImage src={Step3} />
              <h2 className="sectionHeader">Prioritize & Roadmap</h2>
              <div className="sectionDescription">
                Organize features by priority and communicate what your team is working&nbsp;on.
              </div>
              <Link to="/features/roadmap" className="LandingGlobal__cta">
                Read more
              </Link>
            </div>
            <div className="right">
              <LazyLoadedImage
                className="sectionImage"
                src={RoadmapImage}
                alt="Canny features for showing off your product roadmap"
              />
              <div className="featuresGrid">
                <div className="feature">
                  <div className="imageContainer">
                    <LazyLoadedImage src={PrioritizationImage} />
                  </div>
                  <div className="text">
                    <div className="title">Prioritization</div>
                    <div className="description">
                      Score and rank features so you’re building the most impactful&nbsp;things
                    </div>
                  </div>
                </div>
                <div className="feature">
                  <div className="imageContainer">
                    <LazyLoadedImage src={RoadmappingImage} />
                  </div>
                  <div className="text">
                    <div className="title">Roadmapping</div>
                    <div className="description">
                      Create public or private roadmaps and keep everyone on the same&nbsp;page
                    </div>
                  </div>
                </div>
                <div className="feature">
                  <div className="imageContainer">
                    <LazyLoadedImage src={UpdatesImage} />
                  </div>
                  <div className="text">
                    <div className="title">Status Updates</div>
                    <div className="description">
                      Close the feedback loop with status updates sent via&nbsp;email
                    </div>
                  </div>
                </div>
                <div className="feature">
                  <div className="imageContainer">
                    <LazyLoadedImage src={ETAImage} />
                  </div>
                  <div className="text">
                    <div className="title">Date Estimates</div>
                    <div className="description">
                      Set expectations better with an estimation on when the feature will
                      be&nbsp;available
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="featuresContainer">
            <div className="left">
              <LazyLoadedImage src={Step4} />
              <h2 className="sectionHeader">Changelog</h2>
              <div className="sectionDescription">
                Announce product updates to engage people with your newest&nbsp;features.
              </div>
              <Link to="/features/changelog" className="LandingGlobal__cta">
                Read more
              </Link>
            </div>
            <div className="right">
              <LazyLoadedImage
                className="sectionImage"
                src={ChangelogImage}
                alt="Canny features to share your newest features"
              />
              <div className="featuresGrid">
                <div className="feature">
                  <div className="imageContainer">
                    <LazyLoadedImage src={WidgetImage} />
                  </div>
                  <div className="text">
                    <div className="title">Widget</div>
                    <div className="description">
                      Embed updates directly into your product to boost awareness
                      and&nbsp;engagement
                    </div>
                  </div>
                </div>
                <div className="feature">
                  <div className="imageContainer">
                    <LazyLoadedImage src={ScheduleImage} />
                  </div>
                  <div className="text">
                    <div className="title">Schedule Releases</div>
                    <div className="description">
                      Coordinate updates to go out with other marketing&nbsp;efforts
                    </div>
                  </div>
                </div>
                <div className="feature">
                  <div className="imageContainer">
                    <LazyLoadedImage src={MarkdownImage} />
                  </div>
                  <div className="text">
                    <div className="title">Markdown</div>
                    <div className="description">
                      Easily format entries with rich text and inline images for&nbsp;readability
                    </div>
                  </div>
                </div>
                <div className="feature">
                  <div className="imageContainer">
                    <LazyLoadedImage src={LabelsImage} />
                  </div>
                  <div className="text">
                    <div className="title">Labels</div>
                    <div className="description">
                      Enable filtering down to specific areas in your&nbsp;product
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="header center">
            <span className="title">
              Build better products with a dedicated customer feedback&nbsp;tool
            </span>
          </div>
          <LandingFooterCTA style="solid" />
        </div>
      </div>
    );
  }
}

export default compose(withContexts({ trackEvent: TrackEventContext }), hot)(LandingFeaturesPage);
